import {
  Box,
  Chip,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";

/**
 * "Reinstalación AC" de una visita/traslado: indica si el técnico reinstaló el
 * equipo (con cable UTP nuevo) y, de ser así, cuántas reinstalaciones. Genera un
 * reconocimiento económico al técnico, por eso se registra de forma independiente
 * dentro de la Orden de Trabajo. Componente CONTROLADO (editor + readOnly).
 *
 * Se persiste en `agendamiento.observaciones.reinstalacion_ac`.
 */

export type ReinstalacionAc = {
  activo: boolean;
  /** Cantidad de reinstalaciones (entero >= 1) cuando activo = true. */
  cantidad?: number;
};

const ACCENT = "#00695c";

type Props = {
  value?: ReinstalacionAc | null;
  onChange?: (value: ReinstalacionAc) => void;
  readOnly?: boolean;
  title?: string;
};

/** Normaliza el valor persistido (tolerante a null/formatos viejos). */
export function normalizarReinstalacionAc(v: any): ReinstalacionAc {
  if (!v || typeof v !== "object") return { activo: false, cantidad: 0 };
  const activo = !!v.activo;
  const cantidad = activo ? Math.max(1, parseInt(v.cantidad, 10) || 1) : 0;
  return { activo, cantidad };
}

export default function ReinstalacionAcVisita({
  value,
  onChange,
  readOnly = false,
  title = "Reinstalación AC",
}: Props) {
  const activo = !!value?.activo;
  const cantidad = value?.cantidad ?? (activo ? 1 : 0);

  const setActivo = (v: boolean) =>
    onChange && onChange({ activo: v, cantidad: v ? value?.cantidad || 1 : 0 });
  const setCantidad = (n: number) =>
    onChange && onChange({ activo: true, cantidad: n });

  // ---------------- READ-ONLY ----------------
  if (readOnly) {
    return (
      <Box>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
          <BuildOutlinedIcon fontSize="small" sx={{ color: ACCENT }} />
          <Typography variant="subtitle2" fontWeight={700}>
            {title}
          </Typography>
        </Stack>
        {activo ? (
          <Chip
            size="small"
            label={`Sí — ${cantidad} reinstalación${cantidad === 1 ? "" : "es"}`}
            sx={{ fontWeight: 700, bgcolor: "rgba(0,105,92,0.1)", color: ACCENT }}
          />
        ) : (
          <Chip size="small" variant="outlined" label="No" />
        )}
      </Box>
    );
  }

  // ---------------- EDITOR ----------------
  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <BuildOutlinedIcon fontSize="small" sx={{ color: ACCENT }} />
        <Typography variant="subtitle2" fontWeight={700}>
          {title}
        </Typography>
      </Stack>
      <Grid container spacing={1.5} alignItems="center">
        <Grid item xs={12} sm={activo ? 6 : 12}>
          <RadioGroup
            row
            value={activo ? "si" : "no"}
            onChange={(e) => setActivo(e.target.value === "si")}
          >
            <FormControlLabel value="si" control={<Radio size="small" />} label="Sí" />
            <FormControlLabel value="no" control={<Radio size="small" />} label="No" />
          </RadioGroup>
        </Grid>
        {activo && (
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Cantidad de reinstalación"
              inputProps={{ min: 1, step: 1 }}
              value={cantidad || 1}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10);
                setCantidad(isNaN(n) || n < 1 ? 1 : n);
              }}
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
